/**
 * Allowed countries for store switcher.
 * @type {string[]}
 */
const storeAllowedCountries = ['United States', 'United Kingdom'];

/**
 * Fetches the browsing context suggestions from the server.
 * @returns {Promise<Object|null>} The detected values or null if error.
 */
async function fetchBrowsingContext() {
  try {
    const response = await fetch('/browsing_context_suggestions.json');
    if (!response.ok) return null;
    const data = await response.json();
    return data.detected_values;
  } catch {
    // Error intentionally ignored.
    return null;
  }
}

/**
 * Initializes the store switcher logic.
 * Handles message replacement, open/close, and visibility logic.
 * @returns {Promise<void>}
 */
export async function initStoreSwitcher() {
  const detected = await fetchBrowsingContext();
  if (!detected) return;
  const country = detected.country;
  const countryName = storeAllowedCountries.includes(country.name) ? `the ${country.name}` : country.name;
  const countryHandle = country.handle;

  const switcher = document.querySelector('[data-store-switcher]');
  if (!switcher) return;

  const storeMethod = switcher.dataset.storeSwitcherMethod;
  const storeList = switcher.dataset.storeSwitcherList.split(',').map((countryCode) => countryCode.trim());

  // Store IP visibility toggle
  const storeIpSection = document.querySelector('[data-store-switcher-ip-enabled]');
  if (storeIpSection) {
    const enabledHandles = storeIpSection.dataset.geoIpEnabled.split(',');
    if (enabledHandles.includes(countryHandle)) {
      storeIpSection.classList.remove('hide');
    }
  }

  // Update switcher message
  const messageEl = switcher.querySelector('[data-store-switcher-message]');
  const messageTemplate = messageEl?.getAttribute('data-store-switcher-message') || '';
  if (messageEl) {
    messageEl.innerHTML = messageTemplate.replace('[[countryName]]', countryName);
  }

  // Focus handling
  const defaultOption = switcher.querySelector('[data-store-switcher-default-option]');
  const openStoreSwitcher = () => {
    document.querySelector('.store-switcher').classList.add('is-open');
  };
  const focusStoreSwitcher = () => {
    openStoreSwitcher();
    defaultOption?.focus();
  };

  // Dismiss click handlers
  switcher.querySelectorAll('[data-store-switcher-dismiss]').forEach((el) => {
    el.addEventListener('click', () => {
      localStorage.setItem('storeSwitcherDismissed', countryHandle);
      document.querySelector('.store-switcher').classList.remove('is-open');
    });
  });

  // Open triggers
  document.querySelectorAll('[data-store-switcher-open]').forEach((el) => {
    el.addEventListener('click', focusStoreSwitcher);
  });

  const isDismissed = localStorage.getItem('storeSwitcherDismissed') === countryHandle;
  const isInList = storeList.includes(countryHandle);
  const shouldShow =
    !isDismissed && ((storeMethod === 'include' && isInList) || (storeMethod === 'exclude' && !isInList));

  if (shouldShow) openStoreSwitcher();
}

// Auto-initialize on DOMContentLoaded for standard use.
document.addEventListener('DOMContentLoaded', () => {
  initStoreSwitcher();
});
